import { $deleteFromArray, $handleError } from "@/utils";
import service from "@/components/views/smallModels/service/models.service";
import Vue from "vue";
import router from "@/router";
import { ForgeModelVersion } from "@/scripts/js/forgeModels";
import store from "@/store";
import { getForgeToken } from "@/utils/forgeUtils";
import { getRegionString } from "@/utils/regionUtils";
import axios from "axios";
import { log, warn, error } from "@/utils/debugLogger";
import { FACILITY_REQUEST } from '@/utils/http/RequestRegistry';

const Autodesk = window.Autodesk;

export const ADMIN_MODELS = {
	namespaced: true,
	state: {
		entries: [],
		total: 0,
		loading: false,
		masterVersions: [],
		masterVersionsLoading: false,
		thumbnailCache: {}, // Cache for model thumbnails
		options: {
			search: "",
		},
	},
	getters: {
		GET_ENTRIES(state) {
			return state.entries;
		},
		GET_TOTAL(state) {
			return state.total;
		},
		GET_LOADING(state) {
			return state.loading;
		},
		GET_MASTER_VERSIONS(state) {
			return state.masterVersions;
		},
		GET_MASTER_VERSIONS_LOADING(state) {
			return state.masterVersionsLoading;
		},
		GET_OPTIONS(state) {
			return state.options;
		},
		GET_MODEL_THUMBNAIL: (state) => (urn, viewId = null) => {
			const cacheKey = `${urn}${viewId ? `_${viewId}` : ''}`;
			return state.thumbnailCache[cacheKey] || null;
		},
	},
	mutations: {
		SET_ENTRIES(state, value) {
			state.entries = value;
		},
		SET_TOTAL(state, value) {
			state.total = value;
		},
		SET_LOADING(state, value) {
			state.loading = value;
		},
		SET_MASTER_VERSIONS(state, value) {
			state.masterVersions = value;
		},
		SET_MASTER_VERSIONS_LOADING(state, value) {
			state.masterVersionsLoading = value;
		},
		SET_OPTIONS(state, value) {
			state.options = value;
		},
		SET_OPTIONS_STATUS(state, value) {
			if (!value) value = 0;
			state.options.status = value;
		},
		SET_OPTIONS_SEARCH(state, value) {
			state.options.search = value;
		},
		SET_MODEL_THUMBNAIL(state, { urn, viewId, thumbnailUrl }) {
			const cacheKey = `${urn}${viewId ? `_${viewId}` : ''}`;
			Vue.set(state.thumbnailCache, cacheKey, thumbnailUrl);
		},
		UPDATE_MODEL_REGION(state, { modelId, newRegion }) {
			const model = state.entries.find(m => m.id === modelId);
			if (model) {
				model.apsRegion = newRegion;
			}
		},
	},
	actions: {
		GET({ commit, state }) {
			commit("SET_LOADING", true);

			state.options.facilityId = router.currentRoute.params.id;

			service
				.get(state.options)
				.then((res) => {
					let data = res.data.items.map(i => Object.assign(new ForgeModelVersion(), i));

					commit("SET_ENTRIES", data);
					commit("SET_TOTAL", res.total);
				})
				.catch((err) => {
					$handleError(err);
				})
				.finally(() => {
					commit("SET_LOADING", false);
				});
		},
		GET_MASTERS({ commit, state }) {
			commit("SET_LOADING", true);
			state.options.facilityId = router.currentRoute.params.id;
			return service.getMasters(state.options).then((res) => {
				commit("SET_ENTRIES", res.data.items.map(i => Object.assign(new ForgeModelVersion(), i)));
				commit("SET_TOTAL", res.total);
				return res.data.items.map(i => Object.assign(new ForgeModelVersion(), i));
			}).catch((err) => {
				$handleError(err);
				throw err;
			}).finally(() => {
				commit("SET_LOADING", false);
			});
		},
		GET_MASTER_VERSIONS({ commit }, facilityId) {
			commit("SET_MASTER_VERSIONS_LOADING", true);
			return service.getMasterVersions(facilityId, FACILITY_REQUEST)
				.then((res) => {
					const data = res.data.items ? res.data.items.map(i => Object.assign(new ForgeModelVersion(), i)) : res.data || [];
					commit("SET_MASTER_VERSIONS", data);
					return data;
				})
				.catch((err) => {
					commit("SET_MASTER_VERSIONS", []);
					throw err;
				})
				.finally(() => {
					commit("SET_MASTER_VERSIONS_LOADING", false);
				});
		},
		GET_MODEL_VERSIONS(_, { facilityId, modelId }) {
			return service.getModelVersions(facilityId, modelId)
				.then((res) => {
					const data = res.data.items ? res.data.items.map(i => Object.assign(new ForgeModelVersion(), i)) : res.data || [];
					return data;
				});
		},
		DELETE({ state, commit }, id) {
			return service
				.delete(router.currentRoute.params.id, id)
				.then(() => {

					$deleteFromArray(state.entries, id);
					$deleteFromArray(store.state.uploadedModels, id);
					commit("SET_TOTAL", --state.total);

					Vue.prototype.$dialog.notify.success("Deleted Successfully", {
						position: "top-right",
						timeout: 3000,
					});
				})
				.catch((err) => {
					$handleError(err);
				});
		},
		async GET_MODEL_DETAILS(_, { urn, streamingApi = 'streamingV2_EU' }) {
			await new Promise(resolve => setTimeout(resolve, 500));

			return new Promise((resolve, reject) => {
				var options = {
					getAccessToken: getForgeToken,
					env: 'AutodeskProduction2',
					api: streamingApi,
				};

				Autodesk.Viewing.Initializer(options, () => {
					Autodesk.Viewing.Document.load(`urn:${urn}`, (doc) => {
						resolve(doc.getRoot());
					}, (error) => {
						reject(error);
					});
				});
			});
		},
		async GET_MODEL_FULL_DETAILS(_, urn) {
			return new Promise((resolve, reject) => {
				const domain = window.location.origin;
				getForgeToken((access_token) => {
					axios({
						baseURL: `https://cdn.derivative.autodesk.com/modeldata/manifest/${urn}?domain=${domain}`,
						headers: { Authorization: "Bearer " + access_token },
					})
						.then(({ data }) => {
							resolve(data);
						})
						.catch((error) => {
							log(error);
							reject(error);
						});
				});
			});
		},
		GET_MODEL_MANIFEST(_, { urn, apsRegion }) {
			return new Promise((resolve, reject) => {
				getForgeToken((access_token) => {
					axios({
						baseURL: `https://developer.api.autodesk.com/modelderivative/v2/designdata/${urn}/manifest`,
						headers: {
							Authorization: "Bearer " + access_token,
							"x-ads-region": getRegionString(apsRegion)
						},
					})
						.then(({ data }) => {
							let derivatives = data.derivatives;
							let root = derivatives.find((derivative) => derivative.outputType === "svf" || derivative.outputType === "svf2");
							resolve(root);
						})
						.catch((error) => {
							log(error);
							reject(error);
						});
				});
			});
		},
		GET_MODEL_THUMBNAIL({ commit, getters }, { urn, viewId = null, apsRegion = null }) {
			return new Promise((resolve, reject) => {
				// Check cache first
				const cachedThumbnail = getters.GET_MODEL_THUMBNAIL(urn, viewId);
				if (cachedThumbnail) {
					resolve(cachedThumbnail);
					return;
				}

				// Get access token and fetch thumbnail
				getForgeToken((access_token) => {
					try {
						let url = `https://cdn.derivative.autodesk.com/derivativeservice/v2/thumbnails/${urn}?width=600&height=600`;
						if (viewId) {
							url += `&guid=${viewId}`;
						}

						// Build headers with region if provided
						const headers = {
							Authorization: `Bearer ${access_token}`,
							"Content-Type": "application/json",
						};
						if (apsRegion) {
							headers["region"] = getRegionString(apsRegion);
						}

						fetch(url, {
							method: "GET",
							headers,
						})
							.then(response => {
								if (!response.ok) {
									throw new Error(`HTTP error! status: ${response.status}`);
								}
								return response.arrayBuffer();
							})
							.then(buffer => {
								const byteArray = new Uint8Array(buffer);
								const blob = new Blob([byteArray], { type: "image/png" });
								const thumbnailUrl = URL.createObjectURL(blob);

								// Cache the thumbnail
								commit("SET_MODEL_THUMBNAIL", { urn, viewId, thumbnailUrl });
								resolve(thumbnailUrl);
							})
							.catch(error => {
								error("Error fetching thumbnail from Autodesk:", error);
								reject(error);
							});
					} catch (error) {
						error("Error in GET_MODEL_THUMBNAIL:", error);
						reject(error);
					}
				});
			});
		},
		async TRANSLATE_TO_REGION({ commit }, { assetId, modelId, targetRegion }) {
			const response = await service.translateToRegion(assetId, modelId, targetRegion);
			// Update model in state with new region
			commit('UPDATE_MODEL_REGION', { modelId, newRegion: targetRegion });
			return response.data;
		},
	},
};
